import { useState } from "react";
import { Card, Table, Tag, Button, Modal, Form, Input, Select, InputNumber, message, Space, Popconfirm } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, DApp } from "../api";

const STATUS_TAG: Record<number, { label: string; color: string }> = {
  0: { label: "白名单", color: "green" },
  1: { label: "黑名单", color: "red" },
  2: { label: "审核中", color: "orange" },
};

export default function DApps() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  const { data, isLoading } = useQuery({
    queryKey: ["dapps"],
    queryFn: async () => (await api.get<{ items: DApp[] }>("/dapps")).data.items,
  });

  const upsert = useMutation({
    mutationFn: (v: any) => api.post("/dapps", v),
    onSuccess: () => {
      message.success("已保存");
      qc.invalidateQueries({ queryKey: ["dapps"] });
      setOpen(false);
      form.resetFields();
    },
    onError: (e: any) => message.error(e?.response?.data?.error ?? "保存失败"),
  });

  const review = useMutation({
    mutationFn: ({ id, action, risk_score }: { id: string; action: string; risk_score?: number }) =>
      api.post(`/dapps/${id}/review`, { action, risk_score }),
    onSuccess: () => {
      message.success("已处理");
      qc.invalidateQueries({ queryKey: ["dapps"] });
    },
    onError: (e: any) => message.error(e?.response?.data?.error ?? "处理失败"),
  });

  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/dapps/${id}`),
    onSuccess: () => { message.success("已删除"); qc.invalidateQueries({ queryKey: ["dapps"] }); },
    onError: (e: any) => message.error(e?.response?.data?.error ?? "删除失败"),
  });

  return (
    <Card
      style={{ background: "#10131A", border: "1px solid #1A1D26" }}
      title={<span style={{ color: "#F5F5F7" }}>DApp 白名单</span>}
      extra={<Button type="primary" onClick={() => setOpen(true)}>新增 / 更新</Button>}
    >
      <Table<DApp>
        rowKey="id"
        loading={isLoading}
        dataSource={data}
        pagination={{ pageSize: 20 }}
        columns={[
          { title: "名称", dataIndex: "name" },
          {
            title: "URL", dataIndex: "url",
            render: (v: string) => <a href={v} target="_blank" rel="noreferrer">{v}</a>,
          },
          { title: "分类", dataIndex: "category" },
          {
            title: "链", dataIndex: "chains",
            render: (cs: string[] | null) =>
              cs?.map((c) => <Tag key={c}>{c}</Tag>) ?? null,
          },
          {
            title: "状态", dataIndex: "status", width: 100,
            render: (v: number) => <Tag color={STATUS_TAG[v]?.color}>{STATUS_TAG[v]?.label}</Tag>,
          },
          {
            title: "风险评分", dataIndex: "risk_score", width: 100,
            render: (v: number) => (
              <span style={{ color: v < 30 ? "#22C55E" : v < 70 ? "#F59E0B" : "#EF4444", fontWeight: 600 }}>
                {v}
              </span>
            ),
          },
          { title: "访问量", dataIndex: "visit_count", width: 100 },
          {
            title: "操作", width: 260,
            render: (_, r) => (
              <Space>
                <Button size="small" onClick={() => {
                  setOpen(true);
                  form.setFieldsValue({ ...r, chains: r.chains?.join(",") });
                }}>编辑</Button>
                <Button
                  size="small"
                  type="link"
                  disabled={r.status === 0}
                  onClick={() => review.mutate({ id: r.id, action: "approve", risk_score: r.risk_score })}
                >通过</Button>
                <Button
                  size="small"
                  type="link"
                  danger
                  disabled={r.status === 1}
                  onClick={() => review.mutate({ id: r.id, action: "reject", risk_score: Math.max(r.risk_score, 80) })}
                >拒绝</Button>
                <Popconfirm title="确认删除此 DApp?" onConfirm={() => del.mutate(r.id)}>
                  <Button size="small" type="link" danger>删除</Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />

      <Modal
        title="DApp"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={async () => {
          const v = await form.validateFields();
          v.chains = String(v.chains ?? "").split(",").map((s: string) => s.trim()).filter(Boolean);
          upsert.mutate(v);
        }}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="名称" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="url" label="URL" rules={[{ required: true, type: "url" }]}><Input /></Form.Item>
          <Form.Item name="icon_url" label="图标 URL"><Input /></Form.Item>
          <Form.Item name="description" label="描述"><Input.TextArea rows={3} /></Form.Item>
          <Form.Item name="category" label="分类">
            <Select
              options={[
                { value: "DEX", label: "DEX" },
                { value: "NFT", label: "NFT" },
                { value: "DeFi", label: "DeFi" },
                { value: "Tool", label: "Tool" },
                { value: "Game", label: "Game" },
              ]}
            />
          </Form.Item>
          <Form.Item name="chains" label="链(逗号分隔)"><Input placeholder="ethereum,bsc" /></Form.Item>
          <Form.Item name="status" label="状态" initialValue={0}>
            <Select
              options={[
                { value: 0, label: "白名单" },
                { value: 1, label: "黑名单" },
                { value: 2, label: "审核中" },
              ]}
            />
          </Form.Item>
          <Form.Item name="risk_score" label="风险评分(0~100)" initialValue={0}>
            <InputNumber min={0} max={100} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
